// Client-side error tracking init.
//
// Next.js 15.3+/16 loads `instrumentation-client.ts` for browser instrumentation;
// the older `sentry.client.config.ts` is NO LONGER auto-loaded, so without this
// file the browser SDK never initializes and client-side errors are silently
// dropped. Points at Better Stack via NEXT_PUBLIC_SENTRY_DSN (Sentry-compatible
// ingest) — same env var the server init (instrumentation.ts) reads.
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.1";import * as Sentry from '@sentry/nextjs'
import {redactSensitiveUrl} from './lib/sensitiveRoutes'
import {NETWORK_FAILURE_MESSAGES} from './utils/networkFailure'

// Noise that is not a bug in our code. Better Stack has no server-side inbound
// filtering (Sentry's "Inbound Filters" is a Sentry-product feature), and its
// per-pattern Ignore only hides events after we have already paid to ingest
// them — so this has to happen SDK-side, before send.
//
// Kept deliberately narrow. Anything that could be a real defect stays in:
// notably `Failed to fetch` (some of those are on /checkout and may be real
// payment breakage) and ChunkLoadError (can signal a broken deploy, not just a
// stale tab).
const IGNORE_ERRORS = [
  // Expected auth outcomes surfaced as AuthApiError — user typo'd a password
  // or re-registered, not an exception.
  'Invalid login credentials',
  'A user with this email address has already been registered',
  'User already registered',
  // Supabase's send throttle. Regex because the message embeds a countdown
  // ("...after 14 seconds"), so every occurrence is a unique string and these
  // never grouped — each one re-alerted as a brand new issue.
  /For security purposes, you can only request this after \d+ seconds/i,
  // Client-side validation should catch this before we ever call Supabase.
  'Unable to validate email address: invalid format',
  // Safari private mode / partitioned storage refusing localStorage.
  'The operation is insecure.',
  // Native-app bridge probed by in-app webviews we do not control.
  "undefined is not an object (evaluating 'window.webkit.messageHandlers",
  // The Android counterpart: WebView's @JavascriptInterface bridge complaining
  // that the native object behind it has been garbage-collected. Nothing in
  // this app throws these — they come from the in-app browsers (Facebook,
  // Instagram) that a large share of traffic arrives in, and there is nothing
  // on our side to fix. 173 events in one week across both brands, spread over
  // ~45 patterns, i.e. ~45 separate "new error" alerts for the same non-bug.
  'Java object is gone',
  'Java exception was raised during method invocation',
  // Benign ResizeObserver notification, fired by several UI libraries.
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
]

// Scripts that are not ours. Errors thrown inside them are the vendor's or the
// user's extension, and we cannot fix them.
const DENY_URLS = [
  /posthog-recorder\.js/i,
  /\/static\/recorder/i,
  /\bmain\.bundle\.js\b/i,
  /frame_ant/i,
  /tracker\.metricool\.com/i,
  /cdn-cookieyes\.com/i,
  /googletagmanager\.com/i,
  /extension:\/\//i,
  /^chrome:\/\//i,
]

// Chrome for iOS injects its own script into every page, and on some pages it
// recurses until the stack overflows. WebKit reports an injected script with the
// page URL as its filename, never a /_next/ chunk. Its minified names change with
// each Chrome release (Rk, Qk, Lk, Kk…), so every release groups as a "new"
// error and alerts again. 272 events / 17 users in 60 days, all iOS, on every
// kind of page, with not one frame from our bundle. Matched by "no frame of
// ours" rather than by message alone, so a real recursion bug in our code (it
// would have /_next/ frames) still reports.
const STACK_OVERFLOW_RE = /Maximum call stack size exceeded/i

export function is_foreign_stack_overflow(event: Sentry.ErrorEvent): boolean {
  const exceptions = event.exception?.values ?? []
  // Judged over the whole event, so a chained exception (a `cause`) carrying
  // our frames keeps it too.
  const frames = exceptions.flatMap((e) => e.stacktrace?.frames ?? [])
  return (
    exceptions.some((e) => STACK_OVERFLOW_RE.test(e.value ?? '')) &&
    frames.length > 0 &&
    // A frame with no filename could be ours: keep the event.
    frames.every(
      (frame) => frame.filename && !frame.filename.includes('/_next/'),
    )
  )
}

// Search-engine renderers run our client code, but not like a browser does.
// Google's renderer caches JS files far longer than one deploy lives and
// pairs a fresh page with chunks from older builds, so Turbopack throws
// "Module N was instantiated … but the module factory is not available".
// Pattern 81937512: 10 events from 26.10 to 3.11, every one Googlebot, AdsBot or
// GoogleOther on /expert, /blog, /topic. The same renderers account for
// ~470 events across ~30 patterns in 15 days (React #418, "Failed to load
// chunk", "Failed to fetch"). These are not visitor-facing: the pages are
// server-rendered, so what gets indexed does not depend on hydration. Only
// the browser reporter drops them. A server error on a crawler's request
// (instrumentation.ts) is still our bug and still reports.
const CRAWLER_UA_RE =
  /Googlebot|AdsBot-Google|GoogleOther|Google-InspectionTool|Mediapartners-Google|bingbot|Baiduspider|YandexBot|DuckDuckBot|Applebot|HeadlessChrome|\bbot\/\d|compatible;[^)]*\+https?:\/\//i

export function is_crawler_event(
  event: Sentry.ErrorEvent,
  fallback_ua?: string,
): boolean {
  const ua = event.request?.headers?.['User-Agent'] ?? fallback_ua ?? ''
  return CRAWLER_UA_RE.test(ua)
}

// The embedded Sanity Studio (/studio, editors only) holds a dozen long-lived
// fetch streams (/data/listen). When the Mac sleeps, Safari cuts them all, each
// rejects with "TypeError: Load failed" that Studio never catches, and Studio
// then reconnects by itself. Pattern 102ae351: 10 events, one editor's tab,
// ~15 failures at a time after 20–30 min idle, no stack. Only these network
// messages and only on /studio: the same error on /checkout stays reported.
// A stream cut mid-frame shows up differently: @sanity/client gets half a JSON
// message and throws MessageParseError "Unable to parse EventSource message"
// (cause: SyntaxError "Unterminated string"), then reconnects the same way.
// Pattern 8c877c23: 1 event, Safari 26, editor's /studio tab on 2026-11-02.
const STUDIO_PATH_RE = /^(?:https?:\/\/[^/]+)?\/studio(?:[/?#;]|$)/

export function is_studio_network_drop(event: Sentry.ErrorEvent): boolean {
  const exceptions = event.exception?.values ?? []
  if (!STUDIO_PATH_RE.test(event.request?.url ?? '') || !exceptions.length) {
    return false
  }
  const is_network_failure = exceptions.every(
    (e) =>
      e.type === 'TypeError' && NETWORK_FAILURE_MESSAGES.has(e.value ?? ''),
  )
  const is_cut_listen_frame = exceptions.some(
    (e) =>
      e.type === 'MessageParseError' &&
      e.value === 'Unable to parse EventSource message',
  )
  return is_network_failure || is_cut_listen_frame
}

// pages/dovera-zp/checkin-link/[token].tsx's URL embeds a raw signing token
// (a bearer credential for an unauthenticated Art. 9 write) in its path.
// The browser SDK auto-attaches window.location.href to event.request.url,
// to several breadcrumb types, and Next.js instrumentation names
// transactions after the current path — all BEFORE any of our own code runs.
// Opting PostHog out on that page (see [token].tsx) only covers PostHog; this
// covers every client-side Sentry event on the route regardless of source —
// including monitoring.failure('baseline_bundle:submit', e) in
// BaselineBundleEngine.tsx, the common "link went stale mid-fill" path.
// Excludes whitespace too, not just /, ?, # — otherwise a token embedded in
// free-text (a breadcrumb message like "Navigated from A to B") greedily
// swallows everything up to the NEXT slash, including the surrounding words.
const CHECKIN_LINK_URL_RE = /\/dovera-zp\/checkin-link\/[^/?#\s]+/g

// Exported for unit testing (__tests__/instrumentation-client.spec.ts) —
// everything else in this file is Sentry.init's side effect, which nothing
// in the repo currently has a test convention for.
export function redact_checkin_link_url<T extends string | undefined>(
  url: T,
): T {
  if (typeof url !== 'string') return url
  // …and every other route whose path is a credential (SENSITIVE_PATH_SEGMENTS:
  // the slot picker, the preference page, the reminder's attendance answer…),
  // through the same redactor PostHog uses, so one list governs both.
  return redactSensitiveUrl(
    url.replace(CHECKIN_LINK_URL_RE, '/dovera-zp/checkin-link/[redacted]'),
  ) as T
}

// Vercel Skew Protection appends `?dpl=dpl_<id>` to every asset URL, and a
// ChunkLoadError puts that URL in its message — so the same failure became a
// new error pattern (and a new Slack alert) after every deploy. Dropped from
// the message only; stack frames are left alone (symbolication matches on
// debug IDs, not filenames).
const DPL_QUERY_RE = /\?dpl=dpl_[A-Za-z0-9]+/g

export function strip_chunk_error_dpl(event: Sentry.ErrorEvent): void {
  for (const exception of event.exception?.values ?? []) {
    if (exception.type === 'ChunkLoadError' && exception.value) {
      exception.value = exception.value.replace(DPL_QUERY_RE, '')
    }
  }
}

// A page whose code fails to download is stuck: the click that needed it
// does nothing. Usually the file is still there and the download was cut
// off; sometimes the tab outlived the deploy that served it. Either way one
// fresh load fixes it. At most once per RELOAD_WINDOW_MS per tab, and never
// when sessionStorage is unavailable, so it cannot loop.
const CHUNK_RELOAD_KEY = 'ksebe:chunk_reload_at'
const RELOAD_WINDOW_MS = 10 * 60 * 1000

export function should_reload_after_chunk_error(
  reason: unknown,
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now: number,
): boolean {
  if (!(reason instanceof Error) || reason.name !== 'ChunkLoadError') {
    return false
  }
  try {
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY) ?? 0)
    if (now - last < RELOAD_WINDOW_MS) return false
    storage.setItem(CHUNK_RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    let storage: Storage
    try {
      storage = window.sessionStorage
    } catch {
      return
    }
    if (should_reload_after_chunk_error(event.reason, storage, Date.now())) {
      window.location.reload()
    }
  })
}

Sentry.init({
  // vercel-sentry integration historically populated the public counterpart, not
  // SENTRY_DSN; now points at the Better Stack application DSN.
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  // Better Stack binds uploaded source maps to events by release + filename.
  // Without this the browser SDK reported no release at all, so every client
  // frame stayed minified however many maps the build uploaded. Inlined at
  // build time by next.config.shared.js from VERCEL_GIT_COMMIT_SHA.
  release: process.env.NEXT_PUBLIC_APP_RELEASE,

  tracesSampleRate: 0.01,
  debug: false,

  ignoreErrors: IGNORE_ERRORS,
  denyUrls: DENY_URLS,

  beforeSend(event) {
    if (
      is_crawler_event(
        event,
        typeof navigator === 'undefined' ? undefined : navigator.userAgent,
      ) ||
      is_foreign_stack_overflow(event) ||
      is_studio_network_drop(event)
    ) {
      return null
    }
    strip_chunk_error_dpl(event)
    if (event.request?.url) {
      event.request.url = redact_checkin_link_url(event.request.url)
    }
    if (event.request?.headers?.Referer) {
      event.request.headers.Referer = redact_checkin_link_url(
        event.request.headers.Referer,
      )
    }
    for (const crumb of event.breadcrumbs ?? []) {
      if (typeof crumb.data?.url === 'string') {
        crumb.data.url = redact_checkin_link_url(crumb.data.url)
      }
      if (typeof crumb.data?.to === 'string') {
        crumb.data.to = redact_checkin_link_url(crumb.data.to)
      }
      if (typeof crumb.data?.from === 'string') {
        crumb.data.from = redact_checkin_link_url(crumb.data.from)
      }
      if (typeof crumb.message === 'string') {
        crumb.message = redact_checkin_link_url(crumb.message)
      }
    }
    if (event.transaction) {
      event.transaction = redact_checkin_link_url(event.transaction)
    }
    return event
  },

  // Transactions carry the same route-name/URL exposure as error events
  // (event.transaction, request context) — redact there too, not just on
  // captured exceptions.
  beforeSendTransaction(event) {
    if (event.request?.url) {
      event.request.url = redact_checkin_link_url(event.request.url)
    }
    if (event.transaction) {
      event.transaction = redact_checkin_link_url(event.transaction)
    }
    return event
  },
})
